import { Col, Popover, Row, Typography } from 'antd';
import { useMemo } from 'react';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { isNumber } from 'lodash-es';

import { CurrencyFormatWithTitle } from '../../../components/domainSpecific/currencyElements';
import { EMPTY_VALUE } from '../../../common/utils/formatUtils';
import { InlineLink } from '../../../components/typography';
import { WeightFormat } from '../../../components/domainSpecific/measuringUnitsElements';
import { WeightUnit } from '../../../app/enums/measuringUnits';
import { CopyToClipboardButton } from '../../../components/widgets/CopyToClipboard';
import { cssVariables } from '../../../styles/cssVariables';
import { ShipmentChargesContent } from '../../../widgets/shipments/ShipmentChargesContent';
import { useInvoiceStyles } from '../invoice.styles';

const { Paragraph } = Typography;

// Builds the plain-text charges block used when copying to the clipboard.
// Derived from data (not the rendered DOM) so each charge is always on its own
// line regardless of popover layout/visibility timing. `formatCurrency` mirrors
// the on-screen CurrencyFormat so copied amounts match what the user sees.
export function formatChargesForCopy({
  jobNumber,
  charges,
  formatCurrency,
  totalLabel,
}) {
  const lines = [jobNumber];

  (charges?.items ?? []).forEach(item => {
    lines.push(item.name, formatCurrency(item.amount, charges?.currency));
  });

  lines.push(totalLabel, formatCurrency(charges?.total, charges?.currency));

  return lines.join('\n');
}

function JobChargesPopoverTitle({ jobNumber, copyText }) {
  return (
    <Row align="middle">
      <Col flex="1">
        <FormattedMessage
          id="invoice.jobCharges"
          values={{
            jobNumber,
          }}
        />
      </Col>
      <Col>
        <CopyToClipboardButton
          tooltipId="invoice.jobCharges.copy"
          className="icon-14"
          getContent={() => copyText}
        />
      </Col>
    </Row>
  );
}

function JobNumberColumn({ handleRowClick, record }) {
  const intl = useIntl();
  const hasCharges = record?.charges?.items?.length > 0;
  const { styles } = useInvoiceStyles();

  const formatCurrency = (value, currency) =>
    isNumber(value)
      ? intl.formatNumber(value, {
          style: 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : EMPTY_VALUE;

  const copyText = formatChargesForCopy({
    jobNumber: record?.jobNumber,
    charges: record?.charges,
    formatCurrency,
    totalLabel: intl.formatMessage({ id: 'shipmentDetail.charges.total' }),
  });

  return (
    <>
      <Popover
        align={{ offset: [-25, 5] }}
        classNames={{
          root: styles.jobChargesPopover,
          body: styles.jobChargesPopoverBody,
        }}
        content={
          hasCharges ? (
            <div className="job-charges-content">
              <ShipmentChargesContent
                charges={record?.charges}
                currency={record?.charges?.currency}
                rowClassName={styles.jobChargesRow}
                nameColSpan={18}
                amountColSpan={6}
              />
            </div>
          ) : null
        }
        placement="bottomLeft"
        title={
          hasCharges ? (
            <JobChargesPopoverTitle
              jobNumber={record?.jobNumber}
              copyText={copyText}
            />
          ) : null
        }
        styles={{
          root: { width: 260 },
          body: {
            borderRadius: cssVariables.borderRadiusXs,
            boxShadow: `0 5px 10px 0 ${cssVariables.colorGrey6}`,
            color: cssVariables.colorGrey3,
            overflow: 'hidden',
          },
        }}
      >
        <InlineLink
          className="clickable-cell"
          onClick={() => handleRowClick?.(record)}
        >
          {record?.jobNumber}
        </InlineLink>
      </Popover>
      <Paragraph
        ellipsis
        className="font-weight-medium"
        title={record?.bolNumber}
      >
        {record?.bolNumber}
      </Paragraph>
    </>
  );
}

const createInvoiceColumns = (handleRowClick, styles) => [
  {
    key: 'jobNumber',
    title: <FormattedMessage id="invoice.jobbol" />,
    dataIndex: 'jobNumber',
    width: 104,
    ellipsis: true,
    clickable: true,
    render: (_text, record) => (
      <JobNumberColumn handleRowClick={handleRowClick} record={record} />
    ),
  },
  {
    key: 'referenceInfo',
    title: <FormattedMessage id="invoice.shipperConsigneeRef" />,
    dataIndex: 'referenceNumber',
    width: 258,
    ellipsis: true,
    render: (_text, record) => (
      <div className={styles.invoiceReference}>
        <Paragraph ellipsis title={record.shipperFrom}>
          <strong>
            <FormattedMessage id="invoice.reference.from" />
          </strong>
          {record.shipperFrom}
        </Paragraph>
        <Paragraph ellipsis title={record.shipperTo}>
          <strong>
            <FormattedMessage id="invoice.reference.to" />
          </strong>
          {record.shipperTo}
        </Paragraph>
        <Paragraph ellipsis title={record.referenceNumber}>
          <strong>
            <FormattedMessage id="invoice.reference.ref" />
          </strong>
          {record.referenceNumber}
        </Paragraph>
      </div>
    ),
  },
  {
    key: 'commodityInfo',
    title: <FormattedMessage id="invoice.commodity" />,
    dataIndex: 'commodityDescription',
    width: 142,
    ellipsis: true,
    render: (_text, record) => {
      const { commodityDescription, commodityQuantity, commodityWeight } =
        record || {};
      return (
        <div>
          <Paragraph ellipsis title={commodityDescription}>
            {commodityDescription}
          </Paragraph>
          <div>
            <FormattedMessage
              id="labels.pieces"
              values={{ pieces: commodityQuantity }}
            />{' '}
            |{' '}
            <WeightFormat
              value={Number(commodityWeight)}
              unit={WeightUnit.POUND}
            />
          </div>
        </div>
      );
    },
  },
  {
    key: 'billingCharges',
    title: <FormattedMessage id="invoice.billingCharges" />,
    width: 84,
    ellipsis: true,
    render: (_text, record) => {
      const amount = record?.charges?.total;
      if (amount === null) return EMPTY_VALUE;

      const currencyCode = record?.charges?.currency || 'USD';
      return (
        <span className="font-weight-medium">
          <CurrencyFormatWithTitle value={amount} currency={currencyCode} />
        </span>
      );
    },
  },
  {
    key: 'podInfo',
    title: <FormattedMessage id="invoice.podSignedBy" />,
    dataIndex: 'podSignedBy',
    width: 152,
    ellipsis: true,
    render: (_text, record) => {
      const { podSignedBy, podDateTime } = record || {};
      const podDateTimeFormatted = moment(podDateTime).format(
        'MMM-DD-YYYY | HH:mm'
      );

      return (
        <div className="font-weight-medium">
          <Paragraph ellipsis title={podSignedBy}>
            {podSignedBy}
          </Paragraph>
          <Paragraph ellipsis title={podDateTimeFormatted}>
            {podDateTimeFormatted}
          </Paragraph>
        </div>
      );
    },
  },
];

const transformJobToTableRow = (job, invoice) => {
  const commodity = job.commodity?.[0] || {};
  const reference = job.reference?.[0] || {};
  const podInfo = job.podInfo?.[0] || {};

  return {
    id: job.id,
    key: job.id,
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: invoice.invoiceDate,
    jobNumber: job.number,
    bolNumber: job.bolNumber,
    shipperFrom: reference.from,
    shipperTo: reference.to,
    referenceNumber: reference.referenceNumber,
    commodityQuantity: commodity.quantity,
    commodityDescription: commodity.description,
    commodityWeight: commodity.weight,
    commodityWeightUnit: commodity.weightUnit,
    charges: job.charges,
    podSignedBy: podInfo.podSignedBy,
    podDateTime: podInfo.dateTime,
  };
};

const transformInvoiceToTableRow = invoice => ({
  id: invoice.id,
  key: invoice.id,
  ...invoice,
});

const transformInvoiceData = invoiceData => {
  if (!invoiceData?.getInvoice?.data?.length) return [];

  return invoiceData.getInvoice.data.flatMap(invoice => {
    if (invoice.jobs?.length) {
      return invoice.jobs.map(job => transformJobToTableRow(job, invoice));
    }
    return [transformInvoiceToTableRow(invoice)];
  });
};

export const useInvoiceTable = (data, handleRowClick) => {
  const { styles } = useInvoiceStyles();
  const invoiceJobsColumns = useMemo(
    () => createInvoiceColumns(handleRowClick, styles),
    [handleRowClick, styles]
  );

  const invoiceJobsTableData = useMemo(
    () => transformInvoiceData(data),
    [data]
  );

  return {
    invoiceJobsColumns,
    invoiceJobsTableData,
  };
};
